import { API_BASE } from "@/lib/apiClient";

/**
 * Gleitende Session-Verlängerung für die React-Welt.
 *
 * Hintergrund: Das App-JWT/Cookie hat eine feste Lebensdauer (`SESSION_TTL_MINUTES`,
 * in Produktion typ. 8 h). Die klassische App ruft `POST /auth/session/refresh`
 * beim Laden, alle 45 min und beim Zurückkehren in den Tab auf – die React-App tat
 * das bislang NICHT. Dadurch lief die Sitzung trotz aktiver Nutzung aus und der
 * Nutzer musste sich erneut anmelden. Dieses Modul schließt die Lücke.
 *
 * Das Cookie ist HttpOnly; wir können den Token im JS nicht lesen, lösen aber per
 * Refresh-Request serverseitig ein neues, weiter in die Zukunft datiertes Cookie
 * aus (`max_age` wird zurückgesetzt). Best-Effort: Fehler werden ignoriert, die
 * Seite bleibt nutzbar. Offline wird gar nicht erst versucht.
 */

const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 min
// Nicht öfter als alle 2 min tatsächlich refreshen (z. B. bei häufigem Tab-Wechsel).
const MIN_REFRESH_GAP_MS = 2 * 60 * 1000;

let started = false;
let lastRefreshAt = 0;
let inflight: Promise<void> | null = null;

function isOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

export function refreshSessionSilently(force = false): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (!isOnline()) return Promise.resolve();

  const now = Date.now();
  if (!force && now - lastRefreshAt < MIN_REFRESH_GAP_MS) {
    return Promise.resolve();
  }
  if (inflight) return inflight;

  lastRefreshAt = now;
  inflight = (async () => {
    try {
      await fetch(`${API_BASE || ""}/auth/session/refresh`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        // Bewusst KEIN zentraler apiClient: ein fehlgeschlagener Refresh darf
        // niemals den 401-Redirect auf die Login-Seite auslösen.
      });
    } catch {
      // stiller Best-Effort
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Startet die gleitende Session-Erneuerung (idempotent – mehrfache Aufrufe sind
 * unschädlich). Erneuert beim Start, in festem Intervall, beim Sichtbarwerden des
 * Tabs und beim Wiederherstellen der Verbindung.
 */
export function startSessionRefreshLoop(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  void refreshSessionSilently(true);
  window.setInterval(() => void refreshSessionSilently(), REFRESH_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void refreshSessionSilently();
    }
  });

  window.addEventListener("online", () => {
    void refreshSessionSilently(true);
  });
}
